import {Component, computed, effect, inject, OnInit, signal, untracked} from '@angular/core';
import {toObservable} from '@angular/core/rxjs-interop';
import {MaterialModule} from '../../../core/modules/material.module';
import {SharedModule} from '../../../core/modules/shared.module';
import {PagesContainerComponent} from '../pages-container/pages-container.component';
import {NgxSpinnerService} from 'ngx-spinner';
import {FuseConfigService} from '../../../core/services/config.service';
import {FilterComponent} from '../../../core/components/filter/filter.component';
import {EventsService} from '../../../core/services/events.service';
import {HubOverviewService} from '../../../core/services/hubOverview.service';
import {AdditionalFrontUserInfoService} from '../../../core/services/additionalFrontUserInfo.service';
import {UserService} from '../../../core/services/users.service';
import {debounceTime} from 'rxjs';
import * as fileSaver from 'file-saver';
import {SortDirection} from '@angular/material/sort';
import {ParishOrganizationService} from '../../../core/services/parishOrganization.service';
import {DocumentsService} from '../../../core/services/documents.service';

@Component({
  selector: 'app-hub-overview',
  templateUrl: './hub-overview.component.html',
  styleUrls: ['./hub-overview.component.scss'],
  imports: [MaterialModule, SharedModule, PagesContainerComponent, FilterComponent],
  providers: [EventsService, HubOverviewService, UserService]
})
export class HubOverviewComponent implements OnInit {
  private hubOverviewService = inject(HubOverviewService);
  private additionalFrontUserInfoService = inject(AdditionalFrontUserInfoService);
  private eventService = inject(EventsService);
  private userService = inject(UserService);
  private spinner = inject(NgxSpinnerService);
  private fuseConfig = inject(FuseConfigService);
  private parishService = inject(ParishOrganizationService);
  private documentService = inject(DocumentsService);

  hubParticipants = signal([]);
  tableAdditionalFrontUserInfo = signal<any>([]);
  pageIndex = signal(0);
  perPage = signal(50);
  sort = signal(localStorage.getItem('hubOverviewSort') || 'name');
  direction = signal<SortDirection>(localStorage.getItem('hubOverviewSortDirection') === 'desc' ? 'desc' : 'asc');
  search = signal('');
  pagination = signal<any>(null);
  hasEvent = signal<string>('');
  hasLearnPath = signal<string>('');
  tags = signal<(number | string)[]>([]);
  filterData = signal<any>({
    hasEvent: true,
    hasLearnPath: true
  });
  userFiltersFromDB = signal<any>({});
  userFilters = signal<any>({});
  userFilterId = signal(null);
  parishOrganizations = signal<any>([]);
  activities = signal<any>([]);

  displayedColumns = computed(() => [
    'name',
    'parish',
    'event_date',
    'task',
    'statistic',
    'nextTrainingYear',
    'certificate',
    ...this.tableAdditionalFrontUserInfo().map((item: any) => `additional_${item.id}`)
  ]);

  constructor() {
    effect((onCleanup) => {
      const organizationId = this.fuseConfig.organizationId_Sig();
      if (!organizationId) {
        this.filterData.update(f => ({...f, tags: []}));
        return;
      }
      const subscription = this.eventService.getTagByOrganization(organizationId).subscribe(response => {
        this.filterData.update(f => ({...f, tags: response}));
      });
      onCleanup(() => subscription.unsubscribe());
    });

    effect(() => {
      const s = this.search();
      untracked(() => {
        if (this.pageIndex() !== 0) {
          this.pageIndex.set(0);
        }
      });
    });

    const trigger = computed(() => {
      return {
        page: this.pageIndex(),
        size: this.perPage(),
        sort: this.sort(),
        dir: this.direction(),
        search: this.search(),
        hasEvent: this.hasEvent(),
        hasLearnPath: this.hasLearnPath(),
        tags: this.tags(),
        parishOrganizations: this.parishOrganizations(),
        activities: this.activities(),
      };
    });

    toObservable(trigger).pipe(debounceTime(300)).subscribe(() => {
      this.getHubOverview();
    });
  }

  ngOnInit() {
    this.getUserFilters();
    this.getParishOrganizations();
    this.additionalFrontUserInfoService.getTableAdditionalFrontUserInfo().subscribe({
      next: response => {
        this.tableAdditionalFrontUserInfo.set(response);
      }
    });
  }

  getParishOrganizations() {
    this.parishService.parishOrganizationsOpen().subscribe({
      next: (response: any) => {
        let parishOrganizations = response?.parishOrganizations || [];
        parishOrganizations = parishOrganizations.map((parishOrganization) => {
          parishOrganization.name = parishOrganization.name.split('Kirchgemeinde')[1]?.trim() || parishOrganization.name;
          return parishOrganization;
        });
        this.filterData.update(f => ({...f, parishOrganizations: parishOrganizations}));
        let activities = response?.functionType || [];
        this.filterData.update(f => ({...f, activities: activities}));
      }
    });
  }

  getUserFilters() {
    this.userService.getFilter('hubOverview').subscribe((res: any) => {
      if (res) {
        const filters = JSON.parse(res.config);
        this.userFiltersFromDB.set(filters);
        this.userFilters.set(filters);
        this.userFilterId.set(res.id);
        this.tags.set(filters.tags || []);
        this.hasEvent.set(filters.hasEvent || '');
        this.hasLearnPath.set(filters.hasLearnPath || '');
        this.parishOrganizations.set(filters.parishOrganizations || []);
        this.activities.set(filters.activities || []);
        this.search.set(filters.search || '');
        this.fuseConfig.activeFilter_Sig.set(
          this.tags().length > 0 || !!this.hasEvent() || !!this.hasLearnPath() ||
          this.parishOrganizations().length > 0 || this.activities().length > 0
        );
      }
    });
  }

  chooseFilters(e) {
    if (e.choose.hasOwnProperty('hasEvent')) {
      if (e.choose.hasEvent === 'true') {
        this.hasEvent.set('true');
      } else if (e.choose.hasEvent === 'false') {
        this.hasEvent.set('false');
      } else {
        this.hasEvent.set('');
      }
    } else if (e.choose.hasOwnProperty('hasLearnPath')) {
      if (e.choose.hasLearnPath === 'true') {
        this.hasLearnPath.set('true');
      } else if (e.choose.hasLearnPath === 'false') {
        this.hasLearnPath.set('false');
      } else {
        this.hasLearnPath.set('');
      }
    } else if (e.choose.hasOwnProperty('tag')) {
      this.tags.update(tags => e.choose.selected
        ? [...new Set([...tags, e.choose.tag])]
        : tags.filter(tag => tag !== e.choose.tag));
      this.userFiltersFromDB.update(f => ({...f, tags: this.tags()}));
      this.pageIndex.set(0);
    } else if (e.choose.hasOwnProperty('parishOrganization')) {
      this.parishOrganizations.update(parishOrganizations => e.choose.selected
        ? [...new Set([...parishOrganizations, e.choose.parishOrganization])]
        : parishOrganizations.filter(tag => tag !== e.choose.parishOrganization));
      this.userFiltersFromDB.update(f => ({...f, parishOrganizations: this.parishOrganizations()}));
      this.pageIndex.set(0);
    } else if (e.choose.hasOwnProperty('activity')) {
      this.activities.update(activities => e.choose.selected
        ? [...new Set([...activities, e.choose.activity])]
        : activities.filter(activity => activity !== e.choose.activity));
      this.userFiltersFromDB.update(f => ({...f, activities: this.activities()}));
      this.pageIndex.set(0);
    } else if (e.choose === 'all') {
      this.hasEvent.set('');
      this.hasLearnPath.set('');
      this.tags.set([]);
      this.activities.set([]);
      this.parishOrganizations.set([]);
      this.pageIndex.set(0);
      this.filterData.update(f => {
        const {...rest} = f;
        return rest;
      });
    }
    if (e.choose === 'all') {
      this.fuseConfig.activeFilter_Sig.set(false);
    } else {
      this.fuseConfig.activeFilter_Sig.set(true);
    }
    this.setDataFromFilter(e);
  }

  handleSearch(value) {
    this.search.set(value);
    this.setDataFromFilter();
  }

  handlePageEvent(e) {
    this.pageIndex.set(e.pageIndex);
    this.perPage.set(e.pageSize);
  }

  getHubOverview() {
    this.spinner.show();
    this.hubOverviewService.getHubOverview(
      this.pageIndex,
      this.perPage,
      this.sort,
      this.direction,
      this.search,
      this.hasEvent,
      this.hasLearnPath,
      this.tags,
      this.parishOrganizations,
      this.activities
    ).subscribe({
      next: (res: any) => {
        if (res) {
          this.hubParticipants.set(res.users || []);
          this.pagination.set(res.pagination || null);
        }
        this.spinner.hide();
        this.restoreScroll();
      },
      error: () => {
        this.spinner.hide();
      }
    });
  }

  sortData(e) {
    if (!e.direction) {
      this.sort.set('createdAt');
      this.direction.set('desc');
    } else {
      this.sort.set(e.active);
      this.direction.set(e.direction);
    }
    localStorage.setItem('hubOverviewSort', this.sort());
    localStorage.setItem('hubOverviewSortDirection', this.direction());
    this.restoreScroll();
  }

  setDataFromFilter(e?) {
    this.userFilters.set({
      tags: this.tags(),
      hasEvent: this.hasEvent(),
      hasLearnPath: this.hasLearnPath(),
      search: this.search(),
      parishOrganizations: this.parishOrganizations(),
      activities: this.activities()
    });
    this.userFiltersFromDB.set(this.userFilters());
    if ((this as any).filterTimeout) {
      clearTimeout((this as any).filterTimeout);
    }

    (this as any).filterTimeout = setTimeout(() => {
      this.setUserFilters();
    }, 1000);
  }

  setUserFilters() {
    this.userService.setFilters('hubOverview', this.userFilters(), this.userFilterId() || '')
      .subscribe((res: any) => {
      });
  }

  private restoreScroll() {
    localStorage.setItem('hubOverviewScroll', '0');
    const table = document.getElementById('hubOverviewPage');
    if (table) {
      table.scrollTop = 0;
    }
  }

  onScroll(e) {
    localStorage.setItem('hubOverviewScroll', e.target.scrollTop);
  }

  downloadXLSX() {
    this.hubOverviewService.exportUserXLSX(
      this.sort,
      this.direction,
      this.search,
      this.hasEvent,
      this.hasLearnPath,
      this.tags,
      this.parishOrganizations,
      this.activities
    ).subscribe(blob => {
      fileSaver.saveAs(blob, 'HubUsers.xlsx');
    });
  }

  getCertificateDocumentUrl(name: string) {
    return this.documentService.getFileStream(name);
  }
}
